/**
 * DrillSection — the composition wrapper for a drilled cell: find the metric in the
 * catalogue, derive the request level from the URL path (§6 positional grammar), fetch
 * via useDrill, render the kit DrillPanel. Shared by the Overview and Metric Detail
 * surfaces (both drive drill state through useScope). Fetching lives here, not the kit.
 */

import React from 'react'
import DrillPanel from './kit/DrillPanel'
import { deriveLevel } from './kit/drillLevels'
import { useDrill } from '../hooks/useDrill'
import { type Scope } from '../hooks/useScope'
import type { CatalogueResponse, RegistryMetric } from '../types/metrics'

function findMetric(catalogue: CatalogueResponse, metricId: string): RegistryMetric | null {
  for (const group of catalogue.pillars) {
    const metric = group.metrics.find((candidate) => candidate.id === metricId)
    if (metric) return metric
  }
  return null
}

export default function DrillSection({
  scope,
  catalogue,
}: {
  scope: Scope
  catalogue: CatalogueResponse
}): React.JSX.Element {
  const drill = scope.drill! // rendered only when drill state is present
  const metric = findMetric(catalogue, drill.metricId)
  const level = deriveLevel(metric, scope.site, drill.path)
  const query = useDrill(drill, scope.site, level)
  return (
    <DrillPanel
      metricId={drill.metricId}
      metric={metric}
      site={scope.site}
      period={drill.period}
      path={drill.path}
      result={query.data}
      error={query.error}
      loading={query.isPending}
      onNavigate={scope.setDrillPath}
      onClose={scope.closeDrill}
    />
  )
}
